import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';

import { trans } from '@spotahome/soyuz/client';

import { Modal, CloseButton, Button, Input } from '@spotahome/ui-library';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import 'react-phone-input-2/dist/style.css';

import PhoneInput from 'react-phone-input-2';

import graphqlClient from '@gqlClient/client';
import createPlanLeadRequest from '@gqlClient/mutations/landlord';

import { addDataLayerGAEvent } from '@common/utils/DataLayerGAHandler';

import CitySelect from '@components/CitySelector/CitySelect';

import SuccessIcon from '@components/icons/SuccessIcon';

import { pageSourcesEnum } from '../ServicePlans/servicePlanConfig';

import './ArrangeCallModal.scss';

import Badge from './Badge';

// TODO: Use availableCities API
const cities = [
  {
    id: 'barcelona'
  },
  {
    id: 'berlin'
  },
  {
    id: 'brussels'
  },
  {
    id: 'dublin'
  },
  {
    id: 'lisbon'
  },
  {
    id: 'london'
  },
  {
    id: 'madrid'
  },
  {
    id: 'milan'
  },
  {
    id: 'paris'
  },
  {
    id: 'rome'
  },
  {
    id: 'valencia'
  }
];

const leadSource = 'landingStandard';

const ArrangeCallModal = ({ isOpen, handleModalClose, leadType }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [callAppointmentDay, setcallAppointmentDay] = useState(null);
  const [callAppointmentMoment, setcallAppointmentMoment] = useState(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [hasError, setError] = useState({
    name: false,
    phone: false,
    city: false
  });
  const [isSubmitting, setSubmitting] = useState(false);
  const [hasSubmittingError, setSubmittingError] = useState(false);

  const getCities = () => {
    if (!cities) {
      return {};
    }
    return cities.reduce(
      (acc, cur) => ({
        ...acc,
        [trans(`landingpage.label.${cur.id}`)]: cur.id
      }),
      {}
    );
  };

  const clearForm = () => {
    setSubmitting(false);
    setName('');
    setPhone('');
    setCity('');
    setReferralCode('');
    setShowSuccessScreen(false);
    setcallAppointmentDay(null);
    setcallAppointmentMoment(null);
    setError({
      name: false,
      phone: false,
      city: false
    });
  };

  useEffect(() => {
    clearForm();
  }, [isOpen]);

  const handleNameUpdateInput = e => {
    const { value } = e.target;
    setName(value);
  };

  const handlePhoneUpdateInput = value => {
    setPhone(value);
  };

  const handleCityUpdateInput = e => {
    const { value } = e.target;
    setCity(value);
  };

  const handleReferralCodeUpdateInput = e => {
    const { value } = e.target;
    setReferralCode(value);
  };

  const isPhoneValid = number => {
    const phoneNumber = parsePhoneNumberFromString(number);
    return phoneNumber && phoneNumber.isValid();
  };

  const sendGaEvent = action => {
    const gaLabel = 'mainStandardBtn';
    addDataLayerGAEvent('Landlord', action, gaLabel);
  };

  const handleSubmitForm = async e => {
    e.preventDefault();
    sendGaEvent('requestCallback');
    const newHasError = {
      name: name.length === 0,
      phone: !isPhoneValid(phone),
      city: city.length === 0
    };

    setError(newHasError);
    if (Object.values(newHasError).includes(true)) {
      return;
    }
    setSubmitting(true);
    try {
      await createPlanLeadRequest(graphqlClient())({
        input: {
          name,
          phone,
          cityId: city,
          referralCode,
          appointmentDay: callAppointmentDay,
          appointmentMoment: callAppointmentMoment,
          leadType,
          leadSource
        }
      });
      setSubmittingError(false);
      setShowSuccessScreen(true);
      sendGaEvent('requestCallbackSuccess');
    } catch {
      setSubmittingError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDayBadgeClick = day => {
    setcallAppointmentDay(day);
  };

  const handleMomentBadgeClick = timeOfDay => {
    setcallAppointmentMoment(timeOfDay);
  };

  const shouldDisplayTodayBadge = () => {
    const lastAppointmentHour = 20;
    const currentHour = parseFloat(moment().format('HH'));
    return lastAppointmentHour > currentHour;
  };

  const shouldDisplayBadge = (dayChosen, timeChosen) => {
    if (dayChosen === 'tomorrow') {
      return true;
    }

    const currentHour = parseFloat(moment().format('HH'));
    const times = {
      morning: 11,
      noon: 16,
      evening: 20
    };

    return times[timeChosen] > currentHour;
  };

  return (
    <Modal isOpen={isOpen} size="medium" className="arrange-call">
      <CloseButton
        size="small"
        onClick={() => handleModalClose(false)}
        className="arrange-call__close-button"
      />
      {!showSuccessScreen && (
        <>
          <p className="arrange-call__intro-text">
            {trans('landlord.landing.contact-form.title')}
          </p>
          <form className="arrange-call__form" onSubmit={handleSubmitForm}>
            <div className="arrange-call__fieldset">
              <label htmlFor="name-input">
                {trans('landlord.landing.contact-form.name')}
              </label>
              <Input
                size="big"
                name="name"
                onChange={handleNameUpdateInput}
                type="text"
                id="name-input"
                maxLength="50"
              />
              <div className="arrange-call__error-wrapper">
                {hasError.name && (
                  <p className="arrange-call__error-message">
                    {' '}
                    {trans('landlord.landing.contact-form.name.validation')}
                  </p>
                )}
              </div>
            </div>

            <div className="arrange-call__fieldset">
              <label htmlFor="phone-input">
                {trans('landlord.landing.contact-form.phone')}
              </label>
              <PhoneInput
                defaultCountry={'es'}
                value={phone}
                onChange={handlePhoneUpdateInput}
                inputClass="arrange-call__phone-input"
                inputExtraProps={{
                  name: 'phone',
                  id: 'phone-input',
                  required: true
                }}
              />
              <div className="arrange-call__error-wrapper">
                {hasError.phone && (
                  <p className="arrange-call__error-message">
                    {trans('landlord.landing.contact-form.phone.validation')}
                  </p>
                )}
              </div>
            </div>

            <div className="arrange-call__fieldset">
              <label htmlFor="city-input">
                {trans('landlord.landing.contact-form.city')}
              </label>

              <CitySelect
                currentCity={city}
                onCityChange={handleCityUpdateInput}
                cities={getCities(cities)}
                className="arrange-call__city-selector"
              />
              <div className="arrange-call__error-wrapper">
                {hasError.city && (
                  <p className="arrange-call__error-message">
                    {' '}
                    {trans('landlord.landing.contact-form.city.validation')}
                  </p>
                )}
              </div>
            </div>

            <div className="arrange-call__fieldset">
              <label htmlFor="referral-code-input">
                {trans('landlord.landing.contact-form.referral-code')}
              </label>

              <Input
                size="big"
                name="referral-code"
                onChange={handleReferralCodeUpdateInput}
                type="text"
                id="referral-code-input"
                maxLength="20"
              />
            </div>

            <div className="arrange-call__fieldset">
              <label htmlFor="callAppointment">
                {trans('landlord.landing.contact-form.time-to-call')}
              </label>
              <div className="arrange-call__badge-container">
                {shouldDisplayTodayBadge() && (
                  <Badge
                    onSelectBadge={() => handleDayBadgeClick('today')}
                    isSelected={callAppointmentDay === 'today'}
                  >
                    {trans('landlord.landing.contact-form.time-to-call.today')}
                  </Badge>
                )}
                <Badge
                  onSelectBadge={() => handleDayBadgeClick('tomorrow')}
                  isSelected={callAppointmentDay === 'tomorrow'}
                >
                  {trans('landlord.landing.contact-form.time-to-call.tomorrow')}
                </Badge>
              </div>
              {callAppointmentDay && (
                <div className="arrange-call__badge-container">
                  {shouldDisplayBadge(callAppointmentDay, 'morning') && (
                    <Badge
                      onSelectBadge={() => handleMomentBadgeClick('morning')}
                      isSelected={callAppointmentMoment === 'morning'}
                    >
                      {trans(
                        'landlord.landing.contact-form.time-to-call.morning'
                      )}
                    </Badge>
                  )}
                  {shouldDisplayBadge(callAppointmentDay, 'noon') && (
                    <Badge
                      onSelectBadge={() => handleMomentBadgeClick('noon')}
                      isSelected={callAppointmentMoment === 'noon'}
                    >
                      {trans('landlord.landing.contact-form.time-to-call.noon')}
                    </Badge>
                  )}
                  {shouldDisplayBadge(callAppointmentDay, 'evening') && (
                    <Badge
                      onSelectBadge={() => handleMomentBadgeClick('evening')}
                      isSelected={callAppointmentMoment === 'evening'}
                    >
                      {trans(
                        'landlord.landing.contact-form.time-to-call.evening'
                      )}
                    </Badge>
                  )}
                </div>
              )}
            </div>
            <Button
              type="submit"
              size="big"
              color="complementary"
              className="arrange-call__submit-button"
            >
              {isSubmitting
                ? trans('landlord.landing.contact-form.sending')
                : trans('landlord.landing.contact-form.submit')}
            </Button>
            {hasSubmittingError && (
              <p className="arrange-call__server-error-message">
                {' '}
                {trans('server_error.title')}
              </p>
            )}
          </form>
        </>
      )}
      {showSuccessScreen && (
        <div className="arrange-call__success-container">
          <SuccessIcon />
          <p className="arrange-call__success-text">
            {trans('landlord.landing.contact-form.success')}
          </p>

          <Button
            size="big"
            color="complementary"
            onClick={() => handleModalClose(false)}
            className="arrange-call__done-button"
          >
            {trans('landlord.landing.contact-form.success-dismiss')}
          </Button>
        </div>
      )}
    </Modal>
  );
};

ArrangeCallModal.propTypes = {
  isOpen: PropTypes.string.isRequired,
  handleModalClose: PropTypes.func.isRequired,
  cities: PropTypes.shape({}).isRequired,
  leadType: PropTypes.oneOf(Object.values(pageSourcesEnum)).isRequired
};

export default ArrangeCallModal;
